/**
 * Integração com o cliente desktop.
 *
 * O mesmo código roda no navegador e dentro do Electron. Tudo aqui degrada
 * silenciosamente quando `window.nexusDesktop` não existe, de modo que nenhum
 * componente precisa perguntar em qual ambiente está.
 */

export interface DesktopSource {
  id: string
  name: string
  kind: 'screen' | 'window'
  thumbnail: string
  appIcon: string | null
}

interface DesktopApi {
  isDesktop: true
  getSources: () => Promise<DesktopSource[]>
  usarFonte: (id: string | null) => void
  getPlatform: () => Promise<{ platform: string; version: string; isDesktop: boolean }>
  setBadge: (count: number) => void
  flashFrame: () => void
  onShortcut: (handler: (action: 'toggle-mute' | 'toggle-deafen') => void) => () => void
}

declare global {
  interface Window {
    nexusDesktop?: DesktopApi
  }
}

export const desktop = typeof window !== 'undefined' ? window.nexusDesktop : undefined

export const isDesktop = Boolean(desktop)

/** Identificação enviada no handshake do gateway. */
export const clientKind: 'web' | 'desktop' = isDesktop ? 'desktop' : 'web'

/**
 * Atualiza o contador do ícone na barra de tarefas.
 *
 * No navegador não há equivalente confiável, então usamos o título da aba, que
 * é o que as pessoas de fato olham quando o navegador está minimizado.
 */
export function updateBadge(count: number): void {
  if (desktop) {
    desktop.setBadge(count)
    return
  }
  document.title = count > 0 ? `(${count}) Nexus` : 'Nexus'
}

/** Chama atenção quando chega menção com a janela fora de foco. */
export function notifyMention(): void {
  if (document.hasFocus()) return
  desktop?.flashFrame()
}

/**
 * Fontes de captura.
 *
 * No Electron devolvemos a lista com miniaturas, para o usuário escolher a
 * janela vendo o que ela contém. No navegador não há como enumerar: o próprio
 * navegador mostra o seletor dele quando `getDisplayMedia` é chamado.
 */
export async function listCaptureSources(): Promise<DesktopSource[] | null> {
  if (!desktop) return null
  try {
    return await desktop.getSources()
  } catch {
    return null
  }
}

/**
 * Avisa o aplicativo qual monitor ou janela transmitir.
 *
 * Precisa ser chamado imediatamente antes de pedir a captura: o Electron
 * decide a fonte no momento em que `getDisplayMedia` chega, e não tem como
 * ler a escolha de dentro da chamada. No navegador não faz nada, porque ali
 * quem pergunta é o próprio navegador.
 */
export function usarFonteDeCaptura(id: string | null): void {
  desktop?.usarFonte(id)
}

export function onGlobalShortcut(
  handler: (action: 'toggle-mute' | 'toggle-deafen') => void,
): () => void {
  if (!desktop) return () => {}
  return desktop.onShortcut(handler)
}
